import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { DEFAULT_TITLE, NOOP, setMetaDescription, setOgMeta, injectJsonLd } from '@/lib/utils';
import { FeedList } from '@/components/feed/FeedList';
import { RightSidebar } from '@/components/layout/RightSidebar';
import { useVehiclePageView } from '@/hooks/useVehiclePageView';
import { ReadingTime } from '@/components/ui/ReadingTime';
import { useScrollDepth } from '@/hooks/useScrollDepth';
import { ProSubsidyAlertCard } from '@/components/ProSubsidyAlertCard';
import { PRO_ENABLED } from '@/lib/features';

// SEO-115 (Strategist proposal)
// PM E-DRIVE Subsidy Countdown Calculator — /guides/ev-subsidy-countdown

const JSONLD_ID = 'ev-subsidy-countdown-jsonld';

/* ------------------------------------------------------------------ */
/*  PM E-DRIVE scheme constants                                       */
/* ------------------------------------------------------------------ */

/** Subsidy rate: ₹2,500 per kWh of battery capacity */
export const SUBSIDY_RATE_PER_KWH = 2500;

/** Hard cap per e2W vehicle */
export const SUBSIDY_CAP_PER_VEHICLE = 5000;

/** Scheme deadline (extended from 31 Mar 2026 to 31 Jul 2026) */
export const SCHEME_DEADLINE = new Date('2026-07-31T23:59:59+05:30');

/** Cumulative e2W units sold under PM E-DRIVE as of early April 2026 */
export const UNITS_SOLD_LAKH = 22.12;

/** Volume cap for the e2W category under PM E-DRIVE */
export const VOLUME_CAP_LAKH = 24.79;

/** Estimated monthly sales run rate in lakh units (industry avg, Q1 2026) */
export const MONTHLY_RUN_RATE_LAKH = 2.5;

/* ------------------------------------------------------------------ */
/*  Scooter presets                                                    */
/* ------------------------------------------------------------------ */

type ScooterPreset = {
  slug: string;
  label: string;
  batteryKwh: number;
  exShowroom: number; // INR
};

const SCOOTER_PRESETS: ScooterPreset[] = [
  { slug: 'ola-s1-pro',     label: 'Ola S1 Pro',      batteryKwh: 4.0,  exShowroom: 139999 },
  { slug: 'tvs-iqube-st',   label: 'TVS iQube ST',    batteryKwh: 3.4,  exShowroom: 117000 },
  { slug: 'bajaj-chetak',    label: 'Bajaj Chetak',     batteryKwh: 3.2,  exShowroom: 112000 },
  { slug: 'ather-rizta-z',  label: 'Ather Rizta Z',   batteryKwh: 3.7,  exShowroom: 129999 },
  { slug: 'hero-vida-v1',   label: 'Hero Vida V1',    batteryKwh: 3.94, exShowroom: 125000 },
  { slug: 'revolt-rv400',   label: 'Revolt RV400',    batteryKwh: 3.24, exShowroom: 143999 },
  { slug: 'ola-s1-x',       label: 'Ola S1 X',        batteryKwh: 3.0,  exShowroom: 79999  },
  { slug: 'honda-activa-e',  label: 'Honda Activa-e:', batteryKwh: 1.5,  exShowroom: 117000 },
];

/* ------------------------------------------------------------------ */
/*  Exported calculation helpers (testable)                           */
/* ------------------------------------------------------------------ */

/**
 * Calculate PM E-DRIVE subsidy for an e2W: ₹2,500/kWh capped at ₹5,000.
 */
export function calculateSubsidy(batteryKwh: number): number {
  if (!Number.isFinite(batteryKwh) || batteryKwh <= 0) return 0;
  return Math.min(batteryKwh * SUBSIDY_RATE_PER_KWH, SUBSIDY_CAP_PER_VEHICLE);
}

/**
 * Days remaining until the PM E-DRIVE deadline from a given reference date.
 * Returns 0 if the deadline has already passed.
 */
export function daysUntilDeadline(now: Date = new Date()): number {
  const diff = SCHEME_DEADLINE.getTime() - now.getTime();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Estimated days until the volume cap is hit based on a monthly run rate.
 * Returns Infinity if run rate is zero or negative.
 */
export function daysUntilCapHit(
  soldLakh: number = UNITS_SOLD_LAKH,
  capLakh: number = VOLUME_CAP_LAKH,
  monthlyRateLakh: number = MONTHLY_RUN_RATE_LAKH,
): number {
  if (!Number.isFinite(monthlyRateLakh) || monthlyRateLakh <= 0) return Infinity;
  const remaining = capLakh - soldLakh;
  if (remaining <= 0) return 0;
  const months = remaining / monthlyRateLakh;
  return Math.ceil(months * 30);
}

/**
 * Percentage of the volume cap already consumed.
 */
export function volumeCapPercent(
  soldLakh: number = UNITS_SOLD_LAKH,
  capLakh: number = VOLUME_CAP_LAKH,
): number {
  if (!Number.isFinite(capLakh) || capLakh <= 0) return 0;
  return Math.min(100, (soldLakh / capLakh) * 100);
}

/* ------------------------------------------------------------------ */
/*  Formatting helpers                                                 */
/* ------------------------------------------------------------------ */

function formatINR(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 100000) {
    return `₹${(n / 100000).toFixed(2).replace(/\.00$/, '')} Lakh`;
  }
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

/* ------------------------------------------------------------------ */
/*  JSON-LD schemas                                                    */
/* ------------------------------------------------------------------ */

const WEBAPP_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'PM E-DRIVE Subsidy Countdown Calculator — Electric Two-Wheelers (India 2026)',
  applicationCategory: 'FinanceApplication',
  operatingSystem: 'Web',
  description:
    'Live countdown to the PM E-DRIVE subsidy deadline (July 31, 2026) for electric two-wheelers in India. See days remaining, volume cap progress, and calculate your ₹5,000 subsidy on popular e-scooters like Ola S1 Pro, TVS iQube, Bajaj Chetak, and Ather Rizta.',
  author: { '@type': 'Organization', name: 'MotoWalla' },
  datePublished: '2026-04-18',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the PM E-DRIVE subsidy for electric two-wheelers?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'PM E-DRIVE (PM Electric Drive Revolution in Innovative Vehicle Enhancement) is a central government scheme launched in October 2024 to replace the expired FAME-II programme. For electric two-wheelers (e2W), it offers a demand incentive of ₹2,500 per kWh of battery capacity, capped at ₹5,000 per vehicle. The subsidy is applied upfront at the dealership — buyers pay the reduced ex-showroom price directly.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who qualifies for PM E-DRIVE e2W subsidy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Any individual buyer purchasing a new electric two-wheeler from a manufacturer registered under the PM E-DRIVE scheme qualifies. The vehicle must have a battery certified by an approved testing agency and must be registered in India. Commercial fleet purchases are eligible too, though separate caps apply for e-buses and e-three-wheelers under the scheme.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much PM E-DRIVE subsidy will I get on my electric scooter?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The subsidy is ₹2,500 per kWh of battery capacity, with a hard cap of ₹5,000 per vehicle. Most popular electric scooters have batteries of 3–4 kWh, which means the raw calculation (e.g., 3.4 kWh × ₹2,500 = ₹8,500) exceeds the cap — so nearly every mainstream e-scooter gets the full ₹5,000 flat. Only models with very small batteries (under 2 kWh) receive less.',
      },
    },
    {
      '@type': 'Question',
      name: 'When does the PM E-DRIVE subsidy for e2W end?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The official deadline was extended from March 31, 2026 to July 31, 2026. However, the scheme also has a volume cap of 24.79 lakh electric two-wheelers. With over 22 lakh units already sold by early April 2026 and monthly sales running at roughly 2.5 lakh units, the cap could be breached before the date deadline — meaning the subsidy could end weeks early.',
      },
    },
    {
      '@type': 'Question',
      name: 'What replaced the FAME-II subsidy scheme?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'FAME-II expired on March 31, 2024 after multiple extensions. The government launched PM E-DRIVE on October 1, 2024 with a total outlay of ₹10,900 crore over two years (FY 2025–26 and FY 2026–27). The e2W subsidy under PM E-DRIVE is significantly lower than the peak FAME-II rate — FAME-II offered ₹15,000/kWh at its peak before being cut to ₹10,000/kWh in June 2023, versus just ₹2,500/kWh under PM E-DRIVE.',
      },
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  Page component                                                     */
/* ------------------------------------------------------------------ */

export default function EvSubsidyCountdownPage() {
  useScrollDepth('guide', 'ev-subsidy-countdown');
  useVehiclePageView('ev-subsidy-countdown');

  const [selectedSlug, setSelectedSlug] = useState<string>('ola-s1-pro');
  const [now] = useState(() => new Date());

  const selected = SCOOTER_PRESETS.find((p) => p.slug === selectedSlug);

  const daysLeft = useMemo(() => daysUntilDeadline(now), [now]);
  const daysToCapHit = useMemo(() => daysUntilCapHit(), []);
  const capPct = useMemo(() => volumeCapPercent(), []);
  const effectiveDeadlineDays = Math.min(daysLeft, Number.isFinite(daysToCapHit) ? daysToCapHit : daysLeft);

  const subsidy = useMemo(() => calculateSubsidy(selected?.batteryKwh ?? 0), [selected]);
  const effectivePrice = selected ? selected.exShowroom - subsidy : 0;

  useEffect(() => {
    document.title =
      'PM E-DRIVE Subsidy Countdown — Days Left & Savings Calculator for Electric Scooters (2026) | MotoWalla';

    const desc =
      'Live countdown to the PM E-DRIVE e2W subsidy deadline (July 31, 2026). Volume cap 89% consumed — scheme may end early. Calculate your ₹5,000 subsidy on Ola S1 Pro, TVS iQube, Bajaj Chetak, Ather Rizta, and more.';
    setMetaDescription(desc);
    const cleanupOg = setOgMeta({
      title: document.title,
      description: desc,
      url: `${window.location.origin}/guides/ev-subsidy-countdown`,
    });
    injectJsonLd(JSONLD_ID, [WEBAPP_SCHEMA, FAQ_SCHEMA]);

    return () => {
      document.title = DEFAULT_TITLE;
      cleanupOg();
      document.getElementById(JSONLD_ID)?.remove();
    };
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['search', 'ev-subsidy-countdown'],
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      api.search.query({
        q: 'PM E-DRIVE electric two wheeler subsidy India 2026 scooter EV',
        type: 'posts',
        limit: 30,
      }),
  });

  const posts = data?.posts ?? [];

  return (
    <div className="flex">
      <div className="flex-1 border-r border-border-primary">
        <div className="border-b border-border-primary bg-gradient-to-br from-bg-secondary to-bg-primary p-6">
          <div className="max-w-2xl">
            <span className="inline-block rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-400 mb-3">
              Live Tool — PM E-DRIVE Subsidy Deadline Tracker
            </span>
            <h1 className="text-2xl font-bold text-text-primary mb-2">
              PM E-DRIVE Subsidy Countdown Calculator
            </h1>
            <ReadingTime minutes={5} className="mb-2" />
            <p className="text-text-secondary text-sm leading-relaxed">
              The PM E-DRIVE subsidy for electric two-wheelers has been extended to{' '}
              <strong>July 31, 2026</strong> — but the volume cap of{' '}
              <strong>24.79 lakh units</strong> is running out faster than the calendar. Over 22 lakh
              e2Ws have already been sold, consuming nearly 90% of the allocation. At the current
              monthly run rate of ~2.5 lakh units, the cap could be breached{' '}
              <strong>before the official deadline</strong>. Use this tool to see exactly how many
              days remain and calculate your subsidy on any popular electric scooter.
            </p>
          </div>

          {/* Countdown + Volume progress card */}
          <div className="mt-6 rounded-xl border border-border-primary bg-bg-primary/60 p-5">
            {/* Countdown display */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 p-4 text-center">
                <div className="text-[11px] uppercase tracking-wider text-emerald-300">
                  Calendar Deadline
                </div>
                <div className="mt-2 text-3xl font-bold text-emerald-200">
                  {daysLeft} <span className="text-lg font-normal">days</span>
                </div>
                <div className="mt-1 text-xs text-emerald-300/80">until July 31, 2026</div>
              </div>
              <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-4 text-center">
                <div className="text-[11px] uppercase tracking-wider text-amber-300">
                  Est. Volume Cap Hit
                </div>
                <div className="mt-2 text-3xl font-bold text-amber-200">
                  {Number.isFinite(daysToCapHit) ? (
                    <>
                      ~{daysToCapHit} <span className="text-lg font-normal">days</span>
                    </>
                  ) : (
                    'N/A'
                  )}
                </div>
                <div className="mt-1 text-xs text-amber-300/80">
                  at ~{MONTHLY_RUN_RATE_LAKH}L units/month run rate
                </div>
              </div>
            </div>

            {/* Volume progress bar */}
            <div className="mb-5">
              <div className="flex justify-between items-baseline mb-1.5">
                <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                  Volume Cap Consumed
                </span>
                <span className="text-sm font-semibold text-emerald-400">
                  {capPct.toFixed(1)}% ({UNITS_SOLD_LAKH}L of {VOLUME_CAP_LAKH}L)
                </span>
              </div>
              <div className="w-full h-4 rounded-full bg-bg-tertiary/50 border border-border-primary overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-amber-500 transition-all duration-500"
                  style={{ width: `${Math.min(100, capPct)}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-text-tertiary mt-1">
                <span>0L units</span>
                <span>{VOLUME_CAP_LAKH}L cap</span>
              </div>
            </div>

            {effectiveDeadlineDays < daysLeft && (
              <div className="mb-5 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3">
                <p className="text-sm text-red-300 font-medium">
                  Volume cap likely exhausted ~{daysToCapHit} days from now — roughly{' '}
                  {daysLeft - daysToCapHit} days <em>before</em> the July 31 calendar deadline.
                  Subsidy could end early once the 24.79L unit allocation runs out.
                </p>
              </div>
            )}

            {/* Scooter selector */}
            <label className="block mb-4">
              <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                Select an electric scooter
              </span>
              <select
                value={selectedSlug}
                onChange={(e) => setSelectedSlug(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-emerald-400 focus:outline-none"
              >
                {SCOOTER_PRESETS.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.label} — {p.batteryKwh} kWh · {formatINR(p.exShowroom)}
                  </option>
                ))}
              </select>
            </label>

            {/* Savings output */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-border-primary bg-bg-secondary/70 p-3">
                <div className="text-[11px] uppercase tracking-wider text-text-tertiary">Scooter</div>
                <div className="mt-1 text-sm font-semibold text-text-primary">
                  {selected?.label ?? '—'}
                </div>
                <div className="mt-0.5 text-xs text-text-secondary">
                  Battery: {selected?.batteryKwh ?? 0} kWh · Ex-showroom: {formatINR(selected?.exShowroom ?? 0)}
                </div>
              </div>
              <div className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 p-3">
                <div className="text-[11px] uppercase tracking-wider text-emerald-300">
                  PM E-DRIVE Subsidy
                </div>
                <div className="mt-1 text-lg font-bold text-emerald-200">{formatINR(subsidy)}</div>
                <div className="mt-0.5 text-xs text-emerald-300/80">
                  {selected && selected.batteryKwh * SUBSIDY_RATE_PER_KWH > SUBSIDY_CAP_PER_VEHICLE
                    ? `${selected.batteryKwh} kWh × ₹2,500 = ${formatINR(selected.batteryKwh * SUBSIDY_RATE_PER_KWH)} → capped at ₹5,000`
                    : `${selected?.batteryKwh ?? 0} kWh × ₹2,500 = ${formatINR(subsidy)}`}
                </div>
              </div>
              <div className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 p-3">
                <div className="text-[11px] uppercase tracking-wider text-emerald-300">
                  Effective Price
                </div>
                <div className="mt-1 text-lg font-bold text-emerald-200">
                  {formatINR(effectivePrice)}
                </div>
                <div className="mt-0.5 text-xs text-emerald-300/80">
                  after ₹{subsidy.toLocaleString('en-IN')} subsidy deducted
                </div>
              </div>
            </div>

            {/*
              MON-020: Pro Subsidy Alert Card.

              T-188: gated behind PRO_ENABLED. Its CTA reads 'Upgrade to Pro'
              and drives the same uncommissioned checkout that 500s, so it is a
              Pro entry point wherever it happens to live — being on a guide
              page rather than the feed does not exempt it.

              The GATE IS ON THE WRAPPER, not just the card (rule 17): the
              `mt-5` div is spacing that exists only to separate this card, so
              gating the child alone would leave a 20px gap floating in the
              layout — the dangling-separator shape, fourth occurrence.
            */}
            {PRO_ENABLED && (
              <div className="mt-5">
                <ProSubsidyAlertCard vehicleSlug={selectedSlug} vehicleLabel={selected?.label ?? ''} />
              </div>
            )}

            <p className="mt-4 text-xs text-text-tertiary leading-relaxed">
              * Subsidy is applied at the dealership as a direct price reduction. State-level
              incentives (e.g., Delhi, Gujarat, Maharashtra EV policies) may offer additional
              discounts on road tax and registration. Prices shown are base ex-showroom and may vary
              by variant and city.
            </p>
          </div>

          {/* Full comparison table */}
          <h2 className="mt-8 text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-2">
            All E-Scooters — PM E-DRIVE Subsidy & Effective Price
          </h2>
          <div className="overflow-hidden rounded-xl border border-border-primary">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-bg-tertiary/50 text-text-tertiary text-xs uppercase tracking-wider">
                  <th className="px-4 py-2.5 text-left font-semibold">Model</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Battery</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Ex-Showroom</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Subsidy</th>
                  <th className="hidden sm:table-cell px-4 py-2.5 text-right font-semibold">Effective Price</th>
                </tr>
              </thead>
              <tbody>
                {SCOOTER_PRESETS.map((p) => {
                  const sub = calculateSubsidy(p.batteryKwh);
                  const eff = p.exShowroom - sub;
                  const isActive = p.slug === selectedSlug;
                  return (
                    <tr
                      key={p.slug}
                      className={`border-t border-border-primary transition-colors ${
                        isActive ? 'bg-emerald-500/10' : 'bg-bg-primary/50 hover:bg-bg-tertiary/30'
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-text-primary">{p.label}</td>
                      <td className="px-4 py-3 text-right text-text-secondary">{p.batteryKwh} kWh</td>
                      <td className="px-4 py-3 text-right text-text-secondary">{formatINR(p.exShowroom)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-400">
                        {formatINR(sub)}
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-right text-emerald-400">
                        {formatINR(eff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-xs text-text-tertiary leading-relaxed">
            Related: <a href="/guides/ev-charging-highways-india-2026" className="text-emerald-400 hover:underline">EV charging on Indian highways →</a>
            {' · '}
            <a href="/guides/best-ev-under-20-lakh-india-2026" className="text-emerald-400 hover:underline">Best EVs under ₹20 Lakh</a>
            {' · '}
            <a href="/guides/delhi-ev-policy-calculator" className="text-emerald-400 hover:underline">Delhi EV policy calculator</a>
            {' · '}
            <a href="/guides/cafe3-price-impact-calculator" className="text-emerald-400 hover:underline">CAFE-III price impact calculator</a>
          </div>
        </div>

        {/* Editorial section */}
        <section className="border-b border-border-primary p-6 space-y-4">
          <h2 className="text-lg font-bold text-text-primary">
            From FAME-II to PM E-DRIVE — why the subsidy landscape keeps shrinking
          </h2>
          <div className="space-y-3 text-sm text-text-secondary leading-relaxed">
            <p>
              <strong className="text-text-primary">The backstory: FAME-II's messy exit.</strong>{' '}
              FAME-II ran from 2019 to March 31, 2024, offering up to ₹15,000/kWh at its peak before
              being slashed to ₹10,000/kWh in June 2023 — a move that immediately cratered e2W sales
              for two consecutive months. When the scheme finally expired, the industry spent six
              months in limbo until PM E-DRIVE was notified on October 1, 2024 with a dramatically
              lower rate of just ₹2,500/kWh. The signal from the government is clear: EV adoption
              should increasingly stand on its own economics rather than subsidy crutches.
            </p>
            <p>
              <strong className="text-text-primary">Why the volume cap matters more than the calendar.</strong>{' '}
              The July 31 deadline gets the headlines, but the real constraint is the 24.79 lakh unit
              allocation for e2Ws. At the current pace of roughly 2.5 lakh registrations per month —
              bolstered by new launches from Honda (Activa-e:), Bajaj (refreshed Chetak), and Ather
              (Rizta) — the remaining ~2.67 lakh units of headroom could be consumed in about 32 days.
              Once the volume cap is hit, the subsidy stops regardless of the date. Buyers who delay
              their purchase risk paying the full, unsubsidised ex-showroom price even though the
              scheme is technically still "active" on paper.
            </p>
            <p>
              <strong className="text-text-primary">Smaller batteries get short-changed by the formula.</strong>{' '}
              The ₹5,000 cap means any scooter with a battery above 2 kWh — which is virtually every
              mainstream model — receives the same flat ₹5,000. But the subsidy as a percentage of the
              vehicle price varies wildly: it is 6.25% of the Ola S1 X's ₹79,999 sticker but just
              3.5% of the Revolt RV400 at ₹1,43,999. Under FAME-II, a 4 kWh battery could fetch
              ₹40,000 — eight times the current benefit. The only e-scooter that truly gets less than
              the ₹5,000 cap is the Honda Activa-e: with its diminutive swappable 1.5 kWh pack, which
              qualifies for just ₹3,750.
            </p>
            <p>
              <strong className="text-text-primary">What happens when the music stops.</strong>{' '}
              Post-subsidy, ex-showroom prices effectively rise by ₹5,000 for most models. That is
              not catastrophic — but it removes the last psychological incentive for fence-sitters.
              More significantly, OEMs that have been absorbing part of the GST differential (EVs are
              taxed at 5% vs. 28% + cess for ICE) may recalibrate their pricing once the subsidy
              tailwind disappears. Historical precedent from the June 2023 FAME-II cut suggests a
              15–20% month-on-month dip in e2W sales for the first 60 days after a subsidy reduction,
              followed by a gradual recovery as buyers accept the new normal. If you are planning to
              buy an electric scooter in 2026, the rational move is to act before the cap is breached.
            </p>
          </div>
        </section>

        <div className="px-4 py-3 border-b border-border-primary">
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
            Latest EV Subsidy & Electric Scooter News
          </h2>
        </div>

        {error ? (
          <div className="p-8 text-center text-text-secondary">Failed to load articles. Please try again.</div>
        ) : (
          <FeedList posts={posts} isLoading={isLoading} hasMore={false} onLoadMore={NOOP} isLoadingMore={false} />
        )}
      </div>
      <RightSidebar />
    </div>
  );
}
